import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import { Check, X, HelpCircle } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';

export const SwipeCard = ({ question, onAnswer, isTop, totalCards, currentIndex }) => {
  const { t, language } = useLanguage();
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-15, 15]);
  const opacity = useTransform(x, [-200, -100, 0, 100, 200], [0.5, 1, 1, 1, 0.5]);
  
  const yesOpacity = useTransform(x, [0, 100], [0, 1]);
  const noOpacity = useTransform(x, [-100, 0], [1, 0]);

  const handleDragEnd = (_, info) => {
    const threshold = 100;
    if (info.offset.x > threshold) {
      animate(x, 500, { duration: 0.3 });
      setTimeout(() => onAnswer('yes'), 300);
    } else if (info.offset.x < -threshold) {
      animate(x, -500, { duration: 0.3 });
      setTimeout(() => onAnswer('no'), 300);
    } else {
      animate(x, 0, { type: 'spring', stiffness: 500, damping: 30 });
    }
  };

  const handleButtonAnswer = (answer) => {
    if (answer === 'yes') {
      animate(x, 500, { duration: 0.3 });
    } else if (answer === 'no') {
      animate(x, -500, { duration: 0.3 });
    }
    setTimeout(() => onAnswer(answer), answer === 'unknown' ? 0 : 300);
  };

  const questionLabel = language === 'pt' 
    ? `Pergunta ${currentIndex + 1} de ${totalCards}`
    : `Question ${currentIndex + 1} of ${totalCards}`;

  const swipeHint = language === 'pt'
    ? 'Deslize para a direita para Sim, esquerda para Não'
    : 'Swipe right for Yes, left for No';

  if (!isTop) {
    return (
      <Card className="absolute inset-0 rounded-3xl p-8 flex flex-col items-center justify-center bg-card border-2 border-border opacity-50 scale-95">
        <p className="text-center text-muted-foreground">
          {language === 'pt' ? 'Próxima pergunta...' : 'Next question...'}
        </p>
      </Card>
    );
  }

  return (
    <motion.div
      className="absolute inset-0 cursor-grab active:cursor-grabbing swipe-card"
      style={{ x, rotate, opacity }}
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.7}
      onDragEnd={handleDragEnd}
      data-testid="swipe-card"
    >
      <Card className="h-full rounded-3xl p-8 flex flex-col bg-card border-2 border-border shadow-lg dark:neon-glow relative overflow-hidden">
        {/* Swipe indicators */}
        <motion.div
          className="absolute top-6 right-6 bg-green-500 text-white px-4 py-2 rounded-full font-bold text-lg"
          style={{ opacity: yesOpacity }}
        >
          {language === 'pt' ? 'SIM' : 'YES'}
        </motion.div>
        <motion.div
          className="absolute top-6 left-6 bg-red-500 text-white px-4 py-2 rounded-full font-bold text-lg"
          style={{ opacity: noOpacity }}
        >
          {language === 'pt' ? 'NÃO' : 'NO'}
        </motion.div>

        {/* Progress */}
        <div className="mb-6">
          <span className="label-medical">{questionLabel}</span>
          <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-300"
              style={{ width: `${((currentIndex + 1) / totalCards) * 100}%` }}
            />
          </div>
        </div>

        {/* Question */}
        <div className="flex-1 flex items-center justify-center">
          <h2 className="text-2xl font-bold text-center leading-relaxed">
            {language === 'pt' && question.text_pt ? question.text_pt : question.text}
          </h2>
        </div>

        {/* Hint */}
        <p className="text-center text-muted-foreground text-sm mb-6">
          {swipeHint}
        </p>

        {/* Action buttons */}
        <div className="flex items-center justify-center gap-4">
          <Button
            variant="outline"
            size="lg"
            className="rounded-full w-16 h-16 border-2 border-red-500 text-red-500 hover:bg-red-500 hover:text-white transition-all btn-scale"
            onClick={() => handleButtonAnswer('no')}
            data-testid="answer-no"
          >
            <X className="w-7 h-7" />
          </Button>
          
          <Button
            variant="outline"
            size="lg"
            className="rounded-full w-12 h-12 border-2 border-muted-foreground text-muted-foreground hover:bg-muted transition-all btn-scale"
            onClick={() => handleButtonAnswer('unknown')}
            data-testid="answer-unknown"
          >
            <HelpCircle className="w-5 h-5" />
          </Button>
          
          <Button
            variant="outline"
            size="lg"
            className="rounded-full w-16 h-16 border-2 border-green-500 text-green-500 hover:bg-green-500 hover:text-white transition-all btn-scale"
            onClick={() => handleButtonAnswer('yes')}
            data-testid="answer-yes"
          >
            <Check className="w-7 h-7" />
          </Button>
        </div>
      </Card>
    </motion.div>
  );
};
